import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useSetAppointmentServicePrice } from "@/domains/appointment/appointments/shared/hooks/use-set-appointment-service-price";

interface NoPriceService {
  id: string;
  serviceName: string;
  previousPrice?: number;
}

interface SetServicePriceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  noPriceServices: NoPriceService[];
}

export function SetServicePriceDialog({
  open,
  onOpenChange,
  appointmentId,
  noPriceServices,
}: SetServicePriceDialogProps) {
  const { t } = useTranslation("appointments");
  const { setServicePrice, isUpdating } = useSetAppointmentServicePrice();

  const [prices, setPrices] = useState<Record<string, number | undefined>>({});
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Pre-fill prices with 0 whenever the dialog opens or services change
  useEffect(() => {
    if (open) {
      setPrices(Object.fromEntries(noPriceServices.map((s) => [s.id, s.previousPrice ?? 0])));
      setErrors({});
    }
  }, [open, noPriceServices]);

  const handlePriceChange = (serviceId: string, cents: number | undefined) => {
    setPrices((prev) => ({ ...prev, [serviceId]: cents }));
    setErrors((prev) => ({ ...prev, [serviceId]: false }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, boolean> = {};
    let valid = true;
    for (const service of noPriceServices) {
      const price = prices[service.id];
      if (price === undefined || price < 0) {
        newErrors[service.id] = true;
        valid = false;
      }
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const services = noPriceServices.map((service) => ({
      appointmentServiceId: service.id,
      priceInCents: prices[service.id] ?? 0,
    }));

    await setServicePrice(appointmentId, { services, notifyCustomer });
    onOpenChange(false);
    setPrices({});
    setErrors({});
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setPrices({});
      setErrors({});
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("details.actions.setPriceDialog.title")}</DialogTitle>
          <DialogDescription>{t("details.actions.setPriceDialog.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-3">
            {noPriceServices.map((service) => (
              <div key={service.id} className="space-y-1">
                <Label className="text-sm font-medium">{service.serviceName}</Label>
                <CurrencyInput
                  value={prices[service.id]}
                  onValueChange={(cents) => handlePriceChange(service.id, cents)}
                  placeholder={t("details.actions.setPriceDialog.pricePlaceholder")}
                  disabled={isUpdating}
                  className={errors[service.id] ? "border-destructive" : ""}
                />
                {errors[service.id] && (
                  <p className="text-xs text-destructive">
                    {t("details.actions.setPriceDialog.priceRequired")}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <Label className="text-sm font-medium">
              {t("details.actions.setPriceDialog.notifyCustomer")}
            </Label>
            <Switch
              checked={notifyCustomer}
              onCheckedChange={setNotifyCustomer}
              disabled={isUpdating}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isUpdating}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isUpdating}>
            {t("details.actions.setPriceDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
